import { useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useQuery } from '@apollo/client';

import log, { addToLogQueue } from 'common/ScalyrLogger';
import * as K from 'common/constants';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import Page from 'components/common/Page/Page';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import {
  currentDashboardState,
  queryTimeRangeState,
  searchParamState,
} from 'components/common/Managers/RouteStateManager.state';

import { DashboardJSONType } from 'components/dashboards/types';
import DashboardPage from 'components/dashboards/DashboardPage/DashboardPage';
import { processConfig } from 'components/dashboards/dashboardData';
import {
  dashboardFilterConfigListState,
  dashboardState,
  dashboardTabsState,
  rawDashboardState,
} from 'components/dashboards/dashboards.state';
import { GET_CONFIGV2 } from 'components/dashboards/queries';
import { buildUrl } from 'common/functions';
import { isConfigChanged } from 'common/util/graph';
import { UrlParams } from 'common/types';
import { Schema, Validator } from 'jsonschema';
import { activeTeamState } from 'components/account/account.state';
import GraphSchema from 'common/schemas/graph.json';
import { DashboardType } from 'common/graphqlTypes';
import DashboardHeader from '../DashboardPage/DashboardHeader';

const schema: Schema = {
  type: 'array',
  items: GraphSchema as Schema,
};

const validator = new Validator();

function useDashboardValidationSnitch(dashboardName: string, data: { getDashboard: DashboardJSONType }): void {
  const { id, emailAddress } = useRecoilValue(activeTeamState) || {};

  useEffect(() => {
    if (!data?.getDashboard?.graphs) {
      return;
    }

    const graphs = JSON.parse(data.getDashboard.graphs);

    const validationResult = validator.validate(graphs, schema);

    if (validationResult.errors.length) {
      const { errors } = validationResult;
      addToLogQueue(
        {
          _type: 'DashboardGraphValidation',
          errors,
          id,
          email: emailAddress,
          dashboardName,
        },
        'WARN',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dashboardName]);
}

/**
 * Routing component for events pages. This component returns the appropriate events page based on the mode parameter.
 */
function DashboardRouter(): JSX.Element {
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const setSearchParams = useSetSearchParams();
  const dashboardName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_NAME));
  const dashboardParams = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_PARAMS));
  const viewGraph = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_VIEW_GRAPH));
  const { startTimeNs, endTimeNs } = queryTimeRange;
  const dashboard = useRecoilValue(dashboardState(dashboardName));
  const dashboardFilterConfigList = useRecoilValue(dashboardFilterConfigListState);
  const activeTab = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));

  // have to set notifyOnNetworkStatusChange to true to re-render the components
  const { loading, error, data, refetch } = useQuery(GET_CONFIGV2, {
    variables: { name: dashboardName },
    notifyOnNetworkStatusChange: true,
  });
  const dashboardData = data?.getDashboard ?? data?.getDashboardV2;
  useDashboardValidationSnitch(dashboardName, data);

  // If the config data has changed, set the processed dashboard to global state. This forces the LRQRequests built
  // further down the component tree to re-fetch their data when the user refreshes the dashboard.
  const updateRawDashboardAndInitiateDashboardQueries = useRecoilCallback(({ set, snapshot }) => () => {
    const currentDashboard = snapshot.getLoadable(currentDashboardState).getValue();
    const currentTabs = snapshot.getLoadable(dashboardTabsState).getValue();
    const page = snapshot.getLoadable(searchParamState(K.URL_PARAM.DASHBOARD_NAME)).getValue();
    const params = snapshot.getLoadable(searchParamState(K.URL_PARAM.DASHBOARD_PARAMS)).getValue();
    const dashboardFilters = snapshot.getLoadable(searchParamState(K.URL_PARAM.DASHBOARD_FILTERS)).getValue();
    const newRawDashboard = dashboardData ?? null;
    let currentActiveTab = '';
    const tabs: string[] = [];
    if (dashboardData?.configType) {
      const tabs: string[] = [];
      if (dashboardData.configType === DashboardType.TABBED) {
        dashboardData.tabs.forEach((tab: any) => {
          tabs.push(tab.tabName);
        });
      }
      // also checks if active tab in url param is present in list of tabs
      currentActiveTab =
        activeTab && tabs.indexOf(activeTab) > -1
          ? activeTab
          : dashboardData.firstTab?.tabName
          ? dashboardData.firstTab.tabName
          : dashboardData.tabs[0].tabName;
      // set activeTab param only when dashboard config type is tabbed
      if (dashboardData?.configType === DashboardType.TABBED) {
        setSearchParams({ [K.URL_PARAM.DASHBOARD_ACTIVE_TAB]: currentActiveTab } as UrlParams);
      }
      set(dashboardTabsState, tabs);
    }
    // Note that this is what the {@link dashboardState} selector does. We are checking whether the potential future
    // derived state will have changed. This might not be ideal, but it allows us to fix a critical bug without
    // rewriting the processConfig function
    const newDashboard = processConfig(newRawDashboard, params, dashboardFilters, currentActiveTab);
    if (isConfigChanged(newDashboard, currentDashboard) || currentTabs.length !== tabs.length) {
      set(rawDashboardState(page), newRawDashboard);
      setSearchParams({} as Record<K.URL_PARAM, string>, true);
    }
  });
  useEffect(updateRawDashboardAndInitiateDashboardQueries, [dashboardData]);

  if (!dashboardName) {
    return (
      <ErrorPlaceholder
        error={{
          title: 'No Dashboard Found',
          message: 'Please include the name of a dashboard config file in the url, e.g. ?dash=myDashboard.',
        }}
      />
    );
  }

  // If the dashboard data was successfully returned from the server, but has not been set to global state yet, we
  // want to continue loading until the dashboard has been set in global state.
  const isWaitingForGlobalStateUpdate = !error && !dashboard && !!(data?.getDashboard || data?.getDashboardV2);
  if (loading || isWaitingForGlobalStateUpdate) {
    return (
      <Page containerModifier="DashboardPage" docTitle={`${dashboardName} Dashboard`} type="full">
        <div className="DashboardPage__DashboardMain">
          <DashboardHeader dashboardName={dashboardName} graphIndex={-1} hasWriteAccess={false} />
          <LoadingPlaceholder />
        </div>
      </Page>
    );
  }

  if (error) {
    const errorLink = {
      url: buildUrl(K.ROUTE.CONFIG_FILE, {
        [K.URL_PARAM.PATH]: `/dashboards/${dashboardName}`,
        [K.URL_PARAM.ORIGINAL_URL]: window.location.href,
      } as UrlParams),
      text: `Edit ${dashboardName} JSON config file`,
    };
    return (
      <ErrorPlaceholder
        error={{
          title: 'No Dashboard Found',
          message: error.message,
          errorLink,
        }}
      />
    );
  }
  if (!dashboard || !dashboardData) {
    log.error(`Error loading ${dashboardName} dashboard data.`);
    return (
      <ErrorPlaceholder
        error={{ title: 'Unexpected Error', message: 'There was an unexpected error. Please try again.' }}
      />
    );
  }

  return (
    <DashboardPage
      key={dashboardName}
      dashboard={dashboard}
      dashboardName={dashboardName}
      dashboardParams={dashboardParams}
      dashboardFilters={dashboardFilterConfigList}
      startTime={startTimeNs}
      endTime={endTimeNs}
      refetchDashboard={refetch}
      viewGraph={viewGraph || undefined}
    />
  );
}

export default DashboardRouter;
